import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useGetList, useGetOne, useNotify, useRefresh } from 'react-admin';
import { DialogCloseButton } from '@app/misc/DialogCloseButton';
import type { MissionType, MissionTypeId } from '@wwf/sdk';
import { MissionTypeDrawerForm } from './MissionTypeDrawerForm';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DrawerState } from '@app/misc/DrawerContext';
import { useDrawer } from '@app/misc/DrawerContext';
import { CopyAppLinkButton } from '@app/misc/CopyAppLinkButton';

type MissionTypeDrawerMode = 'create' | 'edit' | 'show';

export function useMissionTypeDrawer() {
    const drawer = useDrawer();
    const openDrawer = useCallback(
        (mode: MissionTypeDrawerMode, selectedId: MissionTypeId | null) =>
            drawer.openDrawer(state => (
                <MissionTypeDrawer
                    {...state}
                    mode={mode}
                    selectedId={selectedId}
                />
            )),
        [drawer]
    );
    const closeDrawer = useCallback(() => {
        drawer.closeDrawer();
    }, [drawer]);
    return { openDrawer, closeDrawer };
}

export function MissionTypeDrawer({
    open,
    onClose,
    mode,
    selectedId,
}: DrawerState & {
    mode: MissionTypeDrawerMode;
    selectedId: MissionTypeId | null;
}) {
    const notify = useNotify();
    const refresh = useRefresh();

    const [viewMode, setViewMode] = useState<MissionTypeDrawerMode>(mode);

    useEffect(() => {
        if (open) {
            setViewMode(mode);
        }
    }, [open, mode, selectedId]);

    const title = useMemo(() => {
        if (viewMode === 'create') return 'Create mission type';
        if (viewMode === 'show') return 'Mission type';
        return 'Edit mission type';
    }, [viewMode]);

    const loadRecord = viewMode === 'edit' || viewMode === 'show';

    const { data: selectedRecord, isPending: isPendingSelectedRecord } =
        useGetOne<MissionType>(
            'missionType',
            { id: selectedId ?? undefined },
            { enabled: open && loadRecord && !!selectedId }
        );

    const { data: orgAntennas = [] } = useGetList(
        'antennaSummary',
        {
            pagination: { page: 0, perPage: 1 },
            sort: { field: 'name', order: 'ASC' },
        },
        {
            enabled: open && viewMode === 'show',
        }
    );

    const missionTypeAppAntennaId = orgAntennas[0]?.id as string | undefined;

    const canToggleShowEdit = !!selectedId && viewMode !== 'create';

    return (
        <Box sx={{ position: 'relative', height: '100%' }}>
            <DialogCloseButton onClose={onClose} />
            <Box p={3} pt={4}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={2}
                    gap={1}
                >
                    <Typography variant="h6">{title}</Typography>
                    {canToggleShowEdit &&
                        (viewMode === 'show' ? (
                            <Stack
                                direction={'row'}
                                mb={2}
                                flexWrap={'wrap'}
                                justifyContent={'space-between'}
                                gap={1}
                            >
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<EditIcon />}
                                    onClick={() => setViewMode('edit')}
                                >
                                    Edit
                                </Button>
                                {missionTypeAppAntennaId != null && (
                                    <CopyAppLinkButton
                                        type="missionType"
                                        id={selectedRecord?.id ?? ''}
                                        context={{
                                            antennaId: missionTypeAppAntennaId,
                                        }}
                                    />
                                )}
                            </Stack>
                        ) : (
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<VisibilityIcon />}
                                onClick={() => setViewMode('show')}
                            >
                                View
                            </Button>
                        ))}
                </Stack>

                <MissionTypeDrawerForm
                    mode={viewMode}
                    record={loadRecord ? selectedRecord : undefined}
                    loading={loadRecord && isPendingSelectedRecord}
                    onClose={onClose}
                    onSuccess={() => {
                        refresh();
                        notify('Saved', { type: 'info' });
                    }}
                />
            </Box>
        </Box>
    );
}
